/**
 * The shared date and time formatter (FMT-01, FMT-02, FMT-04, FMT-24): YYYY-MM-DD dates and
 * "2026-06-11 1:16 PM" times, always in Pacific Time (America/Vancouver), whatever the browser's
 * time zone. Date-only values ("2026-10-13", or stored at midnight / noon UTC) keep their day.
 */
export const PACIFIC_TIME_ZONE = 'America/Vancouver';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
/** Date-only values stored as an instant at 00:00 or 12:00 UTC. */
const DATE_ONLY_INSTANT = /^(\d{4}-\d{2}-\d{2})T(00|12):00:00(\.000)?Z$/;

function toDate(value: string | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function pacificParts(date: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: PACIFIC_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).formatToParts(date);
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

/** YYYY-MM-DD, or null when there's no valid value (render with EmptyValue). */
export function formatDate(value: string | Date | null | undefined): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    if (DATE_ONLY.test(value)) return value;
    const dateOnly = value.match(DATE_ONLY_INSTANT);
    if (dateOnly) return dateOnly[1];
  }
  const date = toDate(value);
  if (!date) return null;
  const p = pacificParts(date);
  return `${p.year}-${p.month}-${p.day}`;
}

/** "2026-06-11 1:16 PM" in Pacific Time, or null. */
export function formatDateTime(value: string | Date | null | undefined): string | null {
  if (value == null || value === '') return null;
  const date = toDate(value);
  if (!date) return null;
  const p = pacificParts(date);
  const period = (p.dayPeriod ?? '').replace(/\./g, '').toUpperCase();
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute} ${period}`.trim();
}

/** Today in Pacific Time as YYYY-MM-DD, for date-input defaults and max values. */
export function todayPacific(now: Date = new Date()): string {
  return formatDate(now) as string;
}

/** YYYY-MM-DD `months` months before today (Pacific), for "Last 24 Months" style presets. */
export function monthsAgoPacific(months: number, now: Date = new Date()): string {
  const [y, m, d] = todayPacific(now).split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 - months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}
